import '@customink/design-tokens/build/ci_light/css/variables.css';
import '@customink/pigment-react/lib/colors.css';
import React, {ReactElement} from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect
} from 'react-router-dom';
import StoreLayout from '../../layouts/StoreLayout';
import OrganizerLayout from '../../layouts/OrganizerLayout/index';
import ProductDetailLayout from '../../layouts/ProductDetailLayout';
import FeatureFlags from '../../constants/FeatureFlags';
import NewStore from '../../components/NewStore';
import NewStoreProduct from '../../components/NewStoreProduct';
import SignalProvider from '../../components/SignalProvider/index';
import RerenderOnError from '../ErrorBoundary/RerenderOnError';
import AppErrorRenderer from '../AppErrorRenderer';
import NotFound from '../../components/NotFound';
import OrderSummaryPage from '../../components/OrderSummary';
import OrderConfirmationPage from '../../components/OrderConfirmation';
import {swapEditPath} from '../../utils/helpers';
import GTMInitializer from '../../components/GTMInitializer';
import MobileProvider from '../MobileProvider';
import StoreClosedWrapper from '../StoreClosedWrapper';
import LoginWrapper from '../LoginWrapper';
import BuyupConfirmationLayout from '../../layouts/BuyupConfirmationLayout';
import services from '../../api/remoteServices';

const App: React.FC = (): ReactElement => {
  return (
    <Router basename="/go/">
      <GTMInitializer />
      <Switch>
        <Route path="/signals">
          <SignalProvider featureSettings={FeatureFlags} />
        </Route>
        <Route exact path={['/']}>
          <LoginWrapper>
            <NewStore />
          </LoginWrapper>
        </Route>
        <Route
          path="/:storeSlug/orderSummary/:campaignId"
          render={({match}) => (
            <MobileProvider>
              <LoginWrapper>
                <OrderSummaryPage
                  storeSlug={match.params.storeSlug}
                  campaignId={match.params.campaignId}
                />
              </LoginWrapper>
            </MobileProvider>
          )}
        />
        <Route
          path="/:storeSlug/orderConfirmation/:campaignId"
          render={({match}) => (
            <MobileProvider>
              <LoginWrapper>
                <OrderConfirmationPage
                  storeSlug={match.params.storeSlug}
                  campaignId={match.params.campaignId}
                />
              </LoginWrapper>
            </MobileProvider>
          )}
        />
        <Route
          path="/:storeSlug/confirm/:campaignId"
          render={({match}) => (
            <MobileProvider>
              <LoginWrapper>
                <BuyupConfirmationLayout
                  storeSlug={match.params.storeSlug}
                  campaignId={match.params.campaignId}
                />
              </LoginWrapper>
            </MobileProvider>
          )}
        />
        <Route
          path="/:storeSlug/manage"
          render={({match}) => (
            <MobileProvider>
              <LoginWrapper>
                <OrganizerLayout storeSlug={match.params.storeSlug} />
              </LoginWrapper>
            </MobileProvider>
          )}
        />
        <Route
          path="/:storeSlug/edit"
          render={({location}) => (
            <Redirect to={swapEditPath(location.pathname)} />
          )}
        />
        <Route
          path="/:storeSlug/products/new"
          render={({match}) => (
            <NewStoreProduct storeSlug={match.params.storeSlug} />
          )}
        />
        <Route
          path="/:storeSlug/products/:productId"
          render={({match}) => (
            <StoreClosedWrapper>
              <ProductDetailLayout
                storeSlug={match.params.storeSlug}
                productId={Number(match.params.productId)}
              />
            </StoreClosedWrapper>
          )}
        />
        <Route
          path="/:storeSlug"
          render={({match}) => {
            // PENDING: [Phelan Lucky] temporarily hard-coded, remove once fundraiser is over
            if (match.params.storeSlug === 'phelan-lucky') {
              window.location.href = `${services.stores}/${match.params.storeSlug}`;
              return null;
            }

            return (
              <MobileProvider>
                <StoreClosedWrapper>
                  <StoreLayout storeSlug={match.params.storeSlug} />
                </StoreClosedWrapper>
              </MobileProvider>
            );
          }}
        />
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Router>
  );
};

const WrappedApp: React.FC = (): ReactElement => (
  <RerenderOnError>
    <AppErrorRenderer>
      <App />
    </AppErrorRenderer>
  </RerenderOnError>
);

export default WrappedApp;
